import AgencyHelper from 'features/Agency/agencyHelper';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

DialogCreateAcceptanceView.propTypes = {
    item: PropTypes.object,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogCreateAcceptanceView({ item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const editMode = !!item;
    const [loading, setLoading] = useState(false);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('NOT_FREE');
    const [amount, setAmount] = useState(0);
    const [tax, setTax] = useState('');
    const [vat, setVat] = useState('');
    const [totalAmount, setTotalAmount] = useState(0);
    const [technicalAcceptance, setTechnicalAcceptance] = useState(null);
    const [financialAcceptance, setFinancialAcceptance] = useState(null);

    // MARK: ---- FUNCTIONS ----

    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    const handleSubmit = async () => {
        setReadyToValidate(true);
        if (
            !Utils.isValid(paymentMethod) ||
            !Utils.isValid(amount) ||
            !Utils.isValid(tax) ||
            !Utils.isValid(vat) ||
            !Utils.isValid(totalAmount)
        ) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }
        if (Utils.isValid(amount) && amount < 0) {
            ToastHelper.showError('Chi phí bảo trì/sửa chữa phải lớn hơn 0');
            return;
        }
        if (Utils.isValid(totalAmount) && totalAmount < 0) {
            ToastHelper.showError('Tổng chi phí phát sinh phải lớn hơn 0');
            return;
        }
        if (totalAmount > 0 && !Utils.isValid(financialAcceptance)) {
            ToastHelper.showError('Vui lòng tải lên biên bản nghiệm thu thanh toán');
            return;
        }

        setLoading(true);

        let params = {
            amount,
            tax: tax / 100,
            vat: vat / 100,
            paymentStatus: 'PROCESSING'
        };
        params = _.pickBy(params, (value) => value !== null && value !== undefined && value !== '');
        if (Utils.isValid(technicalAcceptance)) {
            params.technicalAcceptanceDocument = await uploadAsyncFunction(technicalAcceptance);
        }
        if (Utils.isValid(financialAcceptance)) {
            params.financialAcceptanceDocument = await uploadAsyncFunction(financialAcceptance);
        }

        const res = await MaintainerHelper.API.requestUpdateTicket(item.ticketId, params);
        if (_.isBoolean(res) && res) {
            ToastHelper.showSuccess('Nghiệm thu thành công');
            onDone && onDone();
        } else if (_.isString(res)) {
            ToastHelper.showError(res);
        }
        setLoading(false);
    };

    const paymentMethodOptions = [
        { name: 'Miễn phí', value: 'FREE' },
        { name: 'Có phí', value: 'NOT_FREE' },
    ]

    return (
        <div>
            <div className="row m-0 p-0 px-2 mt-4">
                {/* paymentMethod */}
                <div className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Hình thức tính phí', true)}
                    <KTBSDropdown
                        toggleElement={({ showError }) => (
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${showError ? 'button-invalid' : ''}`}
                                type="button"
                            >
                                {_.find(
                                    paymentMethodOptions,
                                    (item) => item.value === paymentMethod
                                )?.name ?? 'Chọn hình thức tính phí'}
                            </button>
                        )}
                        selectedValue={paymentMethod}
                        dropdownMenuItems={paymentMethodOptions}
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        dropdownMenuClassName="overflow-auto w-100"
                        onChange={(value) => {
                            setPaymentMethod(value);
                            if (value === 'FREE') {
                                setAmount(0);
                            }
                        }}
                        enableCheckValid
                        isDropdownForm={true}
                        isValid={Utils.isValid(paymentMethod)}
                        isTouched={readyToValidate}
                        feedbackText={readyToValidate && !Utils.isValid(paymentMethod) ? "Vui lòng chọn hình thức tính phí" : ''}
                    />
                </div>
                {/* amount */}
                <td className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Chi phí bảo trì/sửa chữa', true)}
                    <KTFormInput
                        name="amount"
                        placeholder="Nhập chi phí bảo trì/sửa chữa"
                        type={KTFormInputType.currency}
                        value={amount}
                        onChange={(value) => {
                            setAmount(Math.floor(value));
                            setTotalAmount(Math.floor(value * (1 + tax / 100 + vat / 100)));
                        }}
                        disabled={!Utils.isValid(paymentMethod) || paymentMethod === 'FREE'}
                    />
                </td>
                {/* tax */}
                <div className="col-md-6 mb-4 px-2">
                    {UIHelper.buildFormLabel('Thuế TNCN (%)', true)}
                    <KTBSDropdown
                        name="tax"
                        toggleElement={
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
                                    ${readyToValidate && !Utils.isValid(tax) ? 'button-invalid' : ''}
                                `}
                                type="button"
                            >
                                {
                                    _.find(
                                        AgencyHelper.Data.orderTaxRateOptions,
                                        (taxRate) => taxRate.value == tax
                                    )?.name || "Chọn thuế suất"
                                }
                            </button>
                        }
                        selectedValue={tax}
                        dropdownMenuItems={AgencyHelper.Data.orderTaxRateOptions}
                        dropdownMenuClassName="overflow-auto w-100 position-relative"
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        onChange={(value) => {
                            setTax(Math.floor(value));
                            setTotalAmount(Math.floor(amount * (1 + value / 100 + vat / 100)));
                        }}
                        isDropdownForm={true}
                    />
                </div>
                {/* vat */}
                <div className="col-md-6 mb-4 px-2">
                    {UIHelper.buildFormLabel('Thuế VAT (%)', true)}
                    <KTBSDropdown
                        name="tax"
                        toggleElement={
                            <button
                                className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
                                       ${readyToValidate && !Utils.isValid(vat) ? 'button-invalid' : ''}
                                    `}
                                type="button"
                            >
                                {
                                    _.find(
                                        AgencyHelper.Data.orderTaxRateOptions,
                                        (taxRate) => taxRate.value == vat
                                    )?.name || "Chọn thuế suất"
                                }
                            </button>
                        }
                        selectedValue={vat}
                        dropdownMenuItems={AgencyHelper.Data.orderTaxRateOptions}
                        dropdownMenuClassName="overflow-auto w-100 position-relative"
                        dropdownMenuStyle={{ maxHeight: '30vh' }}
                        onChange={(value) => {
                            setVat(Math.floor(value));
                            setTotalAmount(Math.floor(amount * (1 + tax / 100 + value / 100)));
                        }}
                        isDropdownForm={true}
                    />
                </div>
                {/* totalAmount */}
                <td className="col-md-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Tổng chi phí phát sinh', true)}
                    <KTFormInput
                        name="totalAmount"
                        placeholder="Nhập tổng chi phí phát sinh"
                        type={KTFormInputType.currency}
                        value={totalAmount}
                        onChange={(value) => setTotalAmount(value)}
                        disabled={true}
                    />
                </td>
                {/* technicalAcceptance */}
                <div className="col-12 p-0 px-2">
                    <div>
                        {UIHelper.buildFormLabel('Tải lên biên bản nghiệm thu kỹ thuật')}
                        <label className="">
                            {UIHelper.buildNegativeButton(
                                'Thêm tập tin',
                                () => { },
                                <i className="fa-regular fa-upload" />
                            )}
                            <input
                                type="file"
                                accept={['.pdf']}
                                onChange={(e) => {
                                    const files = e.target.files;
                                    if (!files || files.length === 0) return;
                                    const file = files[0];
                                    if (file.name.length > 128) {
                                        ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                        e.target.value = null;
                                        return;
                                    }
                                    setTechnicalAcceptance(file);
                                }}
                                hidden
                                onClick={(e) => {
                                    e.target.value = null;
                                }}
                            />
                        </label>
                        {technicalAcceptance && (
                            <div className='col-6'>
                                <ImageView
                                    name={technicalAcceptance?.lastModified ?? technicalAcceptance}
                                    path={
                                        _.isString(technicalAcceptance)
                                            ? technicalAcceptance.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                            : technicalAcceptance?.name
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
                {/* financialAcceptance */}
                <div className="col-12 p-2">
                    <div>
                        {UIHelper.buildFormLabel('Tải lên biên bản nghiệm thu thanh toán', amount > 0)}
                        <label className="">
                            {UIHelper.buildNegativeButton(
                                'Thêm tập tin',
                                () => { },
                                <i className="fa-regular fa-upload" />
                            )}
                            <input
                                type="file"
                                accept={['.pdf']}
                                onChange={(e) => {
                                    const files = e.target.files;
                                    if (!files || files.length === 0) return;
                                    const file = files[0];
                                    if (file.name.length > 128) {
                                        ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                        e.target.value = null;
                                        return;
                                    }
                                    setFinancialAcceptance(file);
                                }}
                                hidden
                                onClick={(e) => {
                                    e.target.value = null;
                                }}
                            />
                        </label>
                        {financialAcceptance && (
                            <div className='col-6'>
                                <ImageView
                                    name={financialAcceptance?.lastModified ?? financialAcceptance}
                                    path={
                                        _.isString(financialAcceptance)
                                            ? financialAcceptance.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                            : financialAcceptance?.name
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* actions */}
            <div
                className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
                {UIHelper.buildGreenButton(
                    !editMode ? 'Thêm công việc' : 'Cập nhật',
                    handleSubmit,
                    null,
                    false,
                    loading
                )}
            </div>
        </div>
    );
}

export default DialogCreateAcceptanceView;
